import { atlasTheme, type TGleeThemeColor, type TThemeColor } from '@atlas/uicorn/lib/System/StylesProvider';
import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import styles from './IconBase.module.css';
import cn from 'classnames';
import normalizeCssSize from '@atlas/tool/box/normalizeCssSize';

const IconBase = React.memo(
    React.forwardRef(function IconBase(
        props: TIconBaseProps,
        ref: React.ForwardedRef<TIconBaseRef>,
    ): React.ReactElement {
        const { size, color, background, verticalAlign, className, style, ...elementProps } = props;
        const theme = atlasTheme;

        const inlineStyles: TIconBaseStyle = { ...style };

        inlineStyles['--icon-base-color'] = resolveColor(theme, color);
        inlineStyles['--icon-base-background'] = resolveBackground(theme, background);

        if (size) {
            inlineStyles['--icon-base-size'] = normalizeCssSize(size);
        }

        if (verticalAlign) {
            inlineStyles['--icon-base-vertical-align'] = verticalAlign;
        }

        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 22 22"
                fill="currentColor"
                {...elementProps}
                ref={ref}
                className={cn(styles.iconBase, className)}
                style={inlineStyles}
                data-ui-role="icon-base"
            />
        );
    }),
);

export default IconBase;

// #region Helpers

const resolveColor = (theme: typeof atlasTheme, colorValue?: TThemeColor | TGleeThemeColor): string => {
    if (!colorValue) return 'inherit';

    if (colorValue in theme.color) {
        return theme.color[colorValue as keyof typeof theme.color];
    }

    if (colorValue in theme.gleeColor) {
        return theme.gleeColor[colorValue as keyof typeof theme.gleeColor];
    }

    return 'inherit';
};

const resolveBackground = (theme: typeof atlasTheme, backgroundValue?: TThemeColor): string => {
    if (!backgroundValue) return 'transparent';

    if (backgroundValue in theme.color) {
        return theme.color[backgroundValue as keyof typeof theme.color];
    }

    return 'transparent';
};

// #endregion

// #region Types

export type TIconBaseRef = SVGSVGElement;

type TIconBaseRequiredProps = 'aria-label' | 'children';

export type TIconBaseProps = Omit<
    React.SVGAttributes<SVGElement>,
    TIconBaseRequiredProps | 'width' | 'height' | 'color'
> &
    Required<Pick<React.SVGAttributes<SVGElement>, TIconBaseRequiredProps>> & {
        size?: number;
        color?: TThemeColor | TGleeThemeColor;
        background?: TThemeColor;
        verticalAlign?: React.CSSProperties['verticalAlign'];
    };

export type TIconBaseStyle = React.CSSProperties & {
    '--icon-base-size'?: string;
    '--icon-base-color'?: string;
    '--icon-base-background'?: string;
    '--icon-base-vertical-align'?: React.CSSProperties['verticalAlign'];
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <IconBase aria-label="Icon" {...props}>
                <rect width={22} height={22} />
            </IconBase>
        );
    },
    controls: {
        color: { type: 'color', initial: 'grey9' },
        background: { type: 'color', initial: 'grey6' },
        size: { type: 'number', initial: 60 },
    },
};

// #endregion
